import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';

import Button from '../../Atoms/Button';
import Loader from '../../Atoms/Loader';
import CreditCardForm from '../../Molecules/CreditCardForm';
import PixForm from '../../Molecules/PixForm';
import CouponForm from '../../Molecules/CouponForm';

import './index.sass';

import {
	doPaymentCheckout,
	doPaymentPixCheckout,
	getPayment,
	changePaymentMethod,
	addCouponToOrder,
	clearCouponError,
} from '../../../store/actions';
import { Analytics, Pagarme, Organization } from '../../../shared';

class PayForm extends React.Component {
	constructor(props) {
		super(props);

		const { organization = {} } = props.organization || {};
		this.state = {
			method: Organization.getDefaultPaymentMethod(organization),
			pagarmeLoading: false,
			finished: false,
		};
	}

	componentDidUpdate(prevProps) {
		const { finished, method } = this.state;
		const { payment, onPaid, organization: orgState } = this.props;
		const { loading, status } = payment;
		const { organization = {} } = orgState || {};
		const modalities = Organization.getPaymentModalities(organization);
		const defaultMethod = Organization.getDefaultPaymentMethod(organization);

		if (prevProps.organization.organization !== organization) {
			this.setState({ method: defaultMethod });
		}

		if (!modalities.pix && method === 'pix' && modalities.creditCard) {
			this.setState({ method: 'creditCard' });
		} else if (!modalities.creditCard && method === 'creditCard' && modalities.pix) {
			this.setState({ method: 'pix' });
		}

		if (!finished && !loading && status === 'PAID') {
			this.setState({ finished: true });
			onPaid();
		}
	}

	onPayByCreditCard = async (e, card) => {
		e.preventDefault();
		const self = this;
		const { orderId, organization: orgState, doPaymentCheckout } = this.props;
		const { organization } = orgState;
		const { name: orgName } = organization;

		this.setState({ pagarmeLoading: true });

		try {
			const tokenResponse = await this.createPagarMeToken(card);

			self.setState({ pagarmeLoading: false });
			doPaymentCheckout(orderId, tokenResponse.id);
			Analytics.sendEvent('paid', 'buying', orgName);
		} catch (error) {
			console.error('Token creation error:', error);
			self.setState({ pagarmeLoading: false });
		}

		Analytics.sendEvent('paying', 'buying', orgName);
	}

	createPagarMeToken = async (card) => {
		const { card_number, card_holder_name, card_expiration_month, card_expiration_year, card_cvv } = card;

		const cardData = {
			type: 'card',
			card: {
				number: card_number,
				holder_name: card_holder_name,
				exp_month: parseInt(card_expiration_month, 10),
				exp_year: parseInt(card_expiration_year, 10),
				cvv: card_cvv,
			},
		};

		const publicKey = Pagarme.getPagarmePublicKey();

		try {
			const response = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${publicKey}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(cardData),
			});

			if (!response.ok) {
				let errorMessage = `Token creation failed: ${response.status} ${response.statusText}`;
				try {
					const errorData = await response.json();
					errorMessage = errorData.message || errorData.error || errorMessage;
				} catch (parseError) {
					console.warn('Could not parse error response:', parseError);
				}
				throw new Error(errorMessage);
			}

			const tokenData = await response.json();
			return tokenData;
		} catch (error) {
			console.error('Error creating Pagar.me token:', error);
			throw error;
		}
	}

	onPayByPix = () => {
		const { orderId, organization: orgState, doPaymentPixCheckout } = this.props;
		const { organization } = orgState;
		const { name: orgName } = organization;
		doPaymentPixCheckout(orderId);
		Analytics.sendEvent('paid', 'buying', orgName);
	}

	render () {
		const { orderId, payment, order, onCancel, intl, organization: orgState } = this.props;
		const { method, pagarmeLoading } = this.state;
		const { loading: loadingPayment, error, pixText } = payment;
		const { loading: loadingOrder, couponLoading, couponError, order: orderData = {} } = order;
		const { coupons: appliedCoupons = [] } = orderData;
		const { organization = {} } = orgState || {};
		const modalities = Organization.getPaymentModalities(organization);
		const canSwitchMethod = Organization.canSwitchPaymentMethod(organization);
		const { formatMessage } = intl;
		const messages = defineMessages({
			payByCreditCard: {
				id: 'payForm.payByCreditCard',
				defaultMessage: 'Pagar com cartão de crédito',
			},
			payByPix: {
				id: 'payForm.payByPix',
				defaultMessage: 'Pagar com PIX',
			},
			pay: {
				id: 'payForm.pay',
				defaultMessage: 'Pagar',
			},
			changeAddress: {
				id: 'payForm.changeAddress',
				defaultMessage: 'Alterar endereço',
			},
		});

		if (pagarmeLoading || loadingOrder) {
			return (
				<div className="PayForm__loader">
					<Loader />
				</div>
			);
		}

		return (
			<>
				{method === 'creditCard' && modalities.creditCard ? (
					<CreditCardForm
						error={error}
						loading={pagarmeLoading || loadingPayment || loadingOrder}
						onPay={this.onPayByCreditCard}
					/>
				) : modalities.pix ? (
					<PixForm
						orderId={orderId}
						pixText={pixText}
						error={error}
						loading={pagarmeLoading || loadingPayment || loadingOrder}
						onPay={this.onPayByPix}
					/>
				) : null}
				{!pagarmeLoading && !loadingOrder && (
					<>
						{/* A generated PIX code is bound to the amount charged, so the
						    coupon entry closes once the customer has one in hand. */}
						{!pixText && (
							<CouponForm
								appliedCoupons={appliedCoupons}
								loading={couponLoading}
								error={couponError}
								onApply={(coupon) => this.props.addCouponToOrder(orderId, coupon)}
								onClearError={this.props.clearCouponError}
							/>
						)}
						{canSwitchMethod && (
							<Button
								className="PayForm__change-method"
								color="secondary"
								isLink
								onClick={() => {
									const nextMethod = method === 'pix' ? 'creditCard' : 'pix';
									this.setState({ method: nextMethod });
									this.props.changePaymentMethod(nextMethod);
								}}
								size="medium"
							>
								{method === 'pix'
									? formatMessage(messages.payByCreditCard)
									: formatMessage(messages.payByPix)}
							</Button>
						)}
						<Button
							className="PayForm__cancel"
							color="primary"
							isLink
							onClick={onCancel}
							size="medium"
						>
							{formatMessage(messages.changeAddress)}
						</Button>
					</>
				)}
			</>
		);
	}
}

PayForm.propTypes = {
	orderId: PropTypes.string.isRequired,
	onPaid: PropTypes.func.isRequired,
	onError: PropTypes.func,
	onEditCustomer: PropTypes.func,
	onCancel: PropTypes.func,
	intl: PropTypes.shape().isRequired,
};

PayForm.defaultProps = {
	onError: () => {},
	onEditCustomer: () => {},
	onCancel: () => {},
};

const mapStateToProps = (state) => ({
	payment: state.payment,
	order: state.order,
	organization: state.organization,
	method: state.payment.method,
});

const mapActionsToProps = (dispatch) => ({
	getPayment: (orderId) => dispatch(getPayment(orderId)),
	doPaymentCheckout: (orderId, cardHash) => dispatch(doPaymentCheckout(orderId, cardHash)),
	doPaymentPixCheckout: (orderId) => dispatch(doPaymentPixCheckout(orderId)),
	changePaymentMethod: (method) => dispatch(changePaymentMethod(method)),
	addCouponToOrder: (orderId, coupon) => dispatch(addCouponToOrder(orderId, coupon)),
	clearCouponError: () => dispatch(clearCouponError()),
});

export default connect(mapStateToProps, mapActionsToProps)(injectIntl(PayForm));
